import React, { useState, useRef, useEffect } from 'react';
import { Container, Nav, Form, Button, Col, Row, Card, Modal, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './Claims.css';

export const Reimbursement: React.FC = () => {
  // Environment variables for the front-end API configuration
  const apiBaseUrl = process.env.REACT_APP_APIURL_WEB;
  if (apiBaseUrl === undefined) {
    throw new Error('REACT_APP_APIURL_WEB is not defined in the environment variables');
  }

  const apiPort = process.env.REACT_APP_APIPORT_WEB;
  if (apiPort === undefined) {
    throw new Error('REACT_APP_APIPORT_WEB is not defined in the environment variables');
  }

  // Build the full API URL
  const apiUrl = apiBaseUrl + ':' + apiPort + '/api/submit-claim';

  // File size limits
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file
  const MAX_TOTAL_FILES_SIZE = 25 * 1024 * 1024; // 25 MB total
  const LIMIT_ERROR_MESSAGE =
    'Single files cannot be greater than 10MB and total files cannot be greater than 25MB. ' +
    'You may create a Reimbursement Request with fewer or no files and then additional files can be provided later.';

  const [currentStep, setCurrentStep] = useState(1);
  const [showErrorSummary, setShowErrorSummary] = useState(false);
  
  const [showModal, setShowModal] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isModalClosed, setIsModalClosed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [apiErrorDetails, setApiErrorDetails] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // State to track if user has attempted to go to next step
  const [attemptedNext, setAttemptedNext] = useState(false);

  // Step 1 state
  const [policyType, setPolicyType] = useState('');

  // Step 2 state
  const [dateOfLoss, setDateOfLoss] = useState('');
  const [description, setDescription] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Step 3 state
  const [policyNumber, setPolicyNumber] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');

  // Step 4 state
  const [claimantName, setClaimantName] = useState('');
  const [claimantPhone, setClaimantPhone] = useState('');
  const [claimantEmail, setClaimantEmail] = useState('');
  const [claimantConfirmEmail, setClaimantConfirmEmail] = useState('');
  const [claimantStreet, setClaimantStreet] = useState('');
  const [claimantCity, setClaimantCity] = useState('');
  const [claimantState, setClaimantState] = useState('');
  const [claimantZip, setClaimantZip] = useState('');

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [errorsSummary, setErrorsSummary] = useState<string[]>([]);

  const steps = policyType === 'Security Deposit' 
    ? ['Product Type', 'Security Deposit Form'] 
    : ['Product Type', 'Event Info', 'Participant Info', 'Additional Participant Info (Optional)', 'Review'];

  const states = [
    'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'IA', 'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD',
    'ME', 'MI', 'MN', 'MO', 'MS', 'MT', 'NC', 'ND', 'NE', 'NH',
    'NJ', 'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'WY'
  ];

  // Use a ref to track previous errors for comparison
  const prevErrorsRef = useRef<{ [key: string]: string }>({});

  // Validate fields when their values change
  useEffect(() => {
    // Skip validation for security deposit form
    if (currentStep === 2 && policyType === 'Security Deposit') {
      return;
    }
    
    // Use the validateCurrentStep without setting state directly
    const stepErrors = validateCurrentStep();
    
    // Compare current errors with previous ones
    const prevErrorsJSON = JSON.stringify(prevErrorsRef.current);
    const newErrorsJSON = JSON.stringify(stepErrors);
    
    // Only update errors if they've changed
    if (prevErrorsJSON !== newErrorsJSON) {
      prevErrorsRef.current = stepErrors;
      setErrors(stepErrors);
      
      // Update errorsSummary based on new errors
      updateErrorsSummary(stepErrors);

      // NEW: If showErrorSummary is true and there are no more errors, hide the error summary
      if (showErrorSummary && Object.keys(stepErrors).length === 0 && !errorMessage && !attachmentError) {
        setShowErrorSummary(false);
      }
    }
  }, [currentStep, policyType, dateOfLoss, description, name, phone, email, confirmEmail, 
      street, city, state, zip, claimantName, claimantPhone, claimantEmail, claimantConfirmEmail, 
      claimantStreet, claimantCity, claimantState, claimantZip]); /* removed , policyNumber */

  // For clearing error messages
  const clearErrorMessage = () => {
    setErrorMessage('');
    
    // NEW: Check if we should hide the error summary
    if (showErrorSummary && Object.keys(errors).length === 0 && !attachmentError) {
      setShowErrorSummary(false);
    }
  };      


  // Add a new function to update the errorsSummary
  const updateErrorsSummary = (currentErrors: { [key: string]: string }) => {
    if (Object.keys(currentErrors).length > 0) {
      const summary = Object.entries(currentErrors).map(([key, value]) => {
        // Convert camelCase to readable format
        const fieldName = key.replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .replace('Claimant', 'Claimant ');
          
        // Special handling for email mismatch errors
        if ((key === 'email' || key === 'confirmEmail') && 
            value.includes('must match')) {
          return 'Email addresses do not match. Please check both email fields.';
        } else if ((key === 'claimantEmail' || key === 'claimantConfirmEmail') && 
                   value.includes('must match')) {
          return 'Claimant email addresses do not match. Please check both claimant email fields.';
        }
        
        return `${fieldName}: ${value}`;
      });
      
      // Remove duplicates (especially for email match errors)
      const uniqueSummary = summary.filter((item, index) => summary.indexOf(item) === index);
      setErrorsSummary(uniqueSummary);
    } else {
      setErrorsSummary([]);
      // NEW: If there are no errors in the current field validations, 
      // and there's no error message or attachment error, 
      // hide the error summary completely
      if (showErrorSummary && !errorMessage && !attachmentError) {
        setShowErrorSummary(false);
      }
    }
  };

  // Effect to clear errors when changing steps
  useEffect(() => {
    // Clear error messages when changing steps
    setErrorMessage('');
    setAttachmentError('');
  }, [currentStep]);

  // Effect to update error summary whenever errors change
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const summary = Object.entries(errors).map(([key, value]) => {
        // Convert camelCase to readable format
        const fieldName = key.replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .replace('Claimant', 'Claimant ');
          
        // Special handling for email mismatch errors
        if ((key === 'email' || key === 'confirmEmail') && 
            value.includes('must match')) {
          return 'Email addresses do not match. Please check both email fields.';
        } else if ((key === 'claimantEmail' || key === 'claimantConfirmEmail') && 
                   value.includes('must match')) {
          return 'Claimant email addresses do not match. Please check both claimant email fields.';
        }
        
        return `${fieldName}: ${value}`;
      });
      
      // Remove duplicates (especially for email match errors)
      // Using filter instead of Set to avoid TypeScript error
      const uniqueSummary = summary.filter((item, index) => summary.indexOf(item) === index);
      setErrorsSummary(uniqueSummary);
    } else {
      setErrorsSummary([]);
    }
  }, [errors]);

  const validateStep = (step: number) => {
    // Don't set errors here - just return the validation result
    const stepErrors = validateCurrentStep();
    return Object.keys(stepErrors).length === 0;
  };

  const validateEmail = (email: string) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const validatePhone = (phone: string) => {
    // Remove formatting characters to count only digits
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length === 10;
  };

  const validateZipCode = (zip: string) => {
    // Basic validation: either 5 digits or 5+4 pattern
    const digitsOnly = zip.replace(/\D/g, '');
    return digitsOnly.length === 5 || digitsOnly.length === 9;
  };

  const formatPhoneNumber = (value: string) => {
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, setter: React.Dispatch<React.SetStateAction<string>>) => {
    const formattedPhoneNumber = formatPhoneNumber(e.target.value);
    setter(formattedPhoneNumber);
  };

  const formatZipCode = (value: string) => {
    const zipCode = value.replace(/[^\d]/g, '');
    const zipCodeLength = zipCode.length;
    if (zipCodeLength <= 5) return zipCode;
    return `${zipCode.slice(0, 5)}-${zipCode.slice(5, 9)}`;
  };

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, setter: React.Dispatch<React.SetStateAction<string>>) => {
    const formattedZipCode = formatZipCode(e.target.value);
    setter(formattedZipCode);
  };

  const handleNext = () => {
    // Set flag that user has attempted to proceed
    setAttemptedNext(true);
    // NEW: Show error summary when Next is clicked
    setShowErrorSummary(true);
    
    // Force validation of the current step
    const stepErrors = validateCurrentStep();
    const isValid = Object.keys(stepErrors).length === 0;
    
    if (isValid) {
      // Clear any error message when moving to the next step
      setErrorMessage('');
      setCurrentStep(currentStep + 1);
      // Reset the attempted next flag for the new step
      setAttemptedNext(false);
      // NEW: Hide error summary when moving to next step
      setShowErrorSummary(false);
      window.scrollTo(0, 0); // Scroll to top when changing steps

    } else {
      /* Removed the code setting Error Message since the errors collection should be sufficient setErrorMessage("Please correct all form errors before submitting."); */
      setErrorMessage(''); 
      // Ensure errors are set for display
      prevErrorsRef.current = stepErrors; // Update the ref
      setErrors(stepErrors);
      // Scroll to the error summary
      setTimeout(() => {
        const errorElement = document.getElementById('error-summary');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  // Helper function to validate current step and return errors
  const validateCurrentStep = () => {
    let stepErrors: { [key: string]: string } = {};
    const step = currentStep; // Use the current step from state
    
    switch (step) {
      case 1:
        if (!policyType || policyType === '') stepErrors.policyType = "Product type is required";
        break;
      case 2:
        if (policyType !== 'Security Deposit') {
          if (!dateOfLoss) stepErrors.dateOfLoss = "Date of loss is required";
          if (!description) stepErrors.description = "Description is required";
        }
        break;
      case 3:
        /* if (!policyNumber) stepErrors.policyNumber = "Policy number is required"; */
        if (!name) stepErrors.name = "Name is required";
        if (!phone) stepErrors.phone = "Phone is required";
        if (!validatePhone(phone)) stepErrors.phone = "Please enter a valid phone number";
        if (!email) stepErrors.email = "Email is required";
        if (!validateEmail(email)) stepErrors.email = "Please enter a valid email address";
        if (email !== confirmEmail) stepErrors.confirmEmail = "Emails do not match";
        if (email && confirmEmail && email !== confirmEmail) {
          stepErrors.email = "Email addresses must match";
          stepErrors.confirmEmail = "Email addresses must match";
        }
        if (!street) stepErrors.street = "Street address is required";
        if (!city) stepErrors.city = "City is required";
        if (!state) stepErrors.state = "State is required";
        if (!zip) stepErrors.zip = "ZIP code is required";
        if (zip && !validateZipCode(zip)) stepErrors.zip = "Please enter a valid ZIP code";
        break;
      case 4:
        // Only validate if any field is filled
        if (claimantName || claimantPhone || claimantEmail || claimantStreet) {
          if (!claimantName) stepErrors.claimantName = "Additional Participant name is required";
          if (!claimantPhone) stepErrors.claimantPhone = "Additional Participant phone is required";
          if (claimantPhone && !validatePhone(claimantPhone)) stepErrors.claimantPhone = "Please enter a valid phone number";
          if (!claimantEmail) stepErrors.claimantEmail = "Additional Participant email is required";
          if (claimantEmail && !validateEmail(claimantEmail)) stepErrors.claimantEmail = "Please enter a valid email address";
          if (claimantEmail !== claimantConfirmEmail) stepErrors.claimantConfirmEmail = "Additional Participant emails do not match";
          if (claimantEmail && claimantConfirmEmail && claimantEmail !== claimantConfirmEmail) {
            stepErrors.claimantEmail = "Additional Participant email addresses must match";
            stepErrors.claimantConfirmEmail = "Additional Participant email addresses must match";
          }
          if (!claimantStreet) stepErrors.claimantStreet = "Additional Participant street address is required";
          if (!claimantCity) stepErrors.claimantCity = "Additional Participant city is required";
          if (!claimantState) stepErrors.claimantState = "Additional Participant state is required";
          if (!claimantZip) stepErrors.claimantZip = "Additional Participant ZIP code is required";
          if (claimantZip && !validateZipCode(claimantZip)) stepErrors.claimantZip = "Please enter a valid ZIP code";
        }
        break;
    }
    return stepErrors;
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setErrorMessage('');
    // Reset the attempted next flag when going back
    setAttemptedNext(false);
    // NEW: Hide error summary when going back
    setShowErrorSummary(false);
    window.scrollTo(0, 0); // Scroll to top when changing steps
  };

  // Modify the validateFileSize function to clear attachment errors when valid
  const validateFileSize = (files: File[]): boolean => {
    // Reset attachment error
    setAttachmentError('');
    
    // Check individual file size
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
          setAttachmentError(LIMIT_ERROR_MESSAGE);
          return false;
        }
    }
    
    // Check total size of all files
    const totalSize = [...attachments, ...files].reduce((t, f) => t + f.size, 0);
    if (totalSize > MAX_TOTAL_FILES_SIZE) {
        setAttachmentError(LIMIT_ERROR_MESSAGE);
        return false;
    }
    
    return true;
  };


  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const newFiles = Array.from(event.target.files);
      
      if (validateFileSize(newFiles)) {
        setAttachments([...attachments, ...newFiles]);
      }
    }
  };

  // Update handleRemoveFile to clear attachment errors
  const handleRemoveFile = (index: number) => {
    const updatedAttachments = [...attachments];
    updatedAttachments.splice(index, 1);
    setAttachments(updatedAttachments);
    
    // Clear attachment error if we're now under the size limit
    const totalSize = updatedAttachments.reduce((total, file) => total + file.size, 0);
    if (totalSize <= MAX_TOTAL_FILES_SIZE) {
      setAttachmentError('');
      // NEW: Check if we should hide the error summary
      if (showErrorSummary && Object.keys(errors).length === 0 && !errorMessage) {
        setShowErrorSummary(false);
      }  
    }
  };

  const handleSelectFiles = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleTakePhoto = () => {
    if (photoInputRef.current) {
      photoInputRef.current.click();
    }
  };
    
  const handleSubmit = async () => {
    // Set flag that user has attempted to proceed
    setAttemptedNext(true);
    // NEW: Show error summary when Submit is clicked
    setShowErrorSummary(true);
    
    // First validate all steps
    const step1Valid = validateStep(1);
    const step2Valid = policyType === 'Security Deposit' || validateStep(2);
    const step3Valid = validateStep(3);
    const step4Valid = validateStep(4);
    
    if (step1Valid && step2Valid && step3Valid && step4Valid) {
      // Clear any previous error messages
      setErrorMessage('');
      setApiErrorDetails('');
      
      // Set submitting state to show processing indicator
      setIsSubmitting(true);
      
      const claimData = {
        policyType,
        dateOfLoss: policyType === 'Security Deposit' ? '' : dateOfLoss,
        description: policyType === 'Security Deposit' ? '' : description,
        specialInstructions: policyType === 'Security Deposit' ? '' : specialInstructions,
        policyNumber,
        name,
        phone,
        email,
        address: `${street}, ${city}, ${state} ${zip}`,
        claimantName,
        claimantPhone,
        claimantEmail,
        claimantAddress: claimantStreet ? `${claimantStreet}, ${claimantCity}, ${claimantState} ${claimantZip}` : 'Not provided'
      };

      const formData = new FormData();
      formData.append('claimData', JSON.stringify(claimData));
      
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });

      try {
        console.log("Submitting claim...");
        const response = await fetch('/api/submit-claim', {
          method: 'POST',
          body: formData,
        });

        // Always set submitting to false after response is received
        setIsSubmitting(false);

        if (response.ok) {
          console.log("Reimbursement Request submitted successfully");
          setIsSubmitted(true);
          setIsModalClosed(false);
          setShowModal(true);
        } else {
          console.error("Failed to submit Reimbursement Request");
          
          // Try to get more detailed error information
          try {
            const errorData = await response.json();
            setApiErrorDetails(errorData.message || `Server error: ${response.status} ${response.statusText}`);
          } catch (parseError) {
            setApiErrorDetails(`Server error: ${response.status} ${response.statusText}`);
          }
          
          setShowModal(true);
        }
      } catch (error) {
        console.error("Error submitting Reimbursement Request:", error);
        setIsSubmitting(false);
        setApiErrorDetails("Network error: Unable to connect to the server. Please check your internet connection and try again.");
        setShowModal(true);
      }
    } else {
      // Set error message for validation failures
      /* Removed the code setting Error Message since the errors collection should be sufficient setErrorMessage("Please correct all form errors before submitting."); */
      setErrorMessage('');
      // Determine which step has errors and navigate to it
      if (!step1Valid) {
        setCurrentStep(1);
      } else if (!step2Valid && policyType !== 'Security Deposit') {
        setCurrentStep(2);
      } else if (!step3Valid) {
        setCurrentStep(3);
      } else if (!step4Valid) {
        setCurrentStep(4);
      }
      
      // Scroll to the error summary
      setTimeout(() => {
        const errorElement = document.getElementById('error-summary');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    if (isSubmitted) {
      setIsModalClosed(true);
    }
  };

  const handleNewClaim = () => {
    // Reset all state variables to their initial values
    setDateOfLoss('');
    setDescription('');
    setSpecialInstructions('');
    setAttachments([]);
    setPolicyNumber('');
    setPolicyType('');
    setName('');
    setPhone('');
    setEmail('');
    setConfirmEmail('');
    setStreet('');
    setCity('');
    setState('');
    setZip('');
    setClaimantName('');
    setClaimantPhone('');
    setClaimantEmail('');
    setClaimantConfirmEmail('');
    setClaimantStreet('');
    setClaimantCity('');
    setClaimantState('');
    setClaimantZip('');
    setErrors({});
    setErrorsSummary([]);
    setErrorMessage('');
    setAttachmentError('');
    setApiErrorDetails('');
    setAttemptedNext(false);
    setShowErrorSummary(false);
    setCurrentStep(1);
    setIsSubmitted(false);
    setIsModalClosed(false);
  };

  // Helper function to render tooltips
  const renderTooltip = (content: string) => (
    <Tooltip id={`tooltip-${content.substr(0, 10)}`}>
      {content}
    </Tooltip>
  );

  // Helper function to render form fields with tooltips when needed
  const renderFieldWithTooltip = (
    label: string, 
    value: string, 
    setter: React.Dispatch<React.SetStateAction<string>>, 
    errorKey: string,
    tooltipText?: string,
    type: string = "text",
    placeholder: string = ""
  ) => {
    const hasError = !!errors[errorKey];
    const isRequired = label.includes("*");
    
    const fieldContent = (
      <Form.Group as={Row} className="mb-3">
        <Form.Label column sm={3} className="inputPrompt">
          {label}
        </Form.Label>
        <Col sm={5}>
          <Form.Control
            type={type}
            value={value}
            onChange={(e) => setter(e.target.value)}
            isInvalid={hasError}
            isValid={value.length > 0 && !hasError}
            placeholder={placeholder}
            required={isRequired}
            className={isRequired ? "required-field" : ""}
          />
          <Form.Control.Feedback type="invalid">{errors[errorKey]}</Form.Control.Feedback>
        </Col>
      </Form.Group>
    );

    if (tooltipText) {
      return (
        <OverlayTrigger placement="right" overlay={renderTooltip(tooltipText)}>
          {fieldContent}
        </OverlayTrigger>
      );
    }

    return fieldContent;
  };

  const renderField = (label: string, value: string, error?: string) => (
    <Form.Group as={Row} className="mb-3">
      <Form.Label column sm={3} className='inputPrompt'>{label} {error && <span className="text-danger">*</span>}</Form.Label>
      <Col sm={5}>
      {value.length - (value.replace(/\n/g,'').length) === value.length ? 
        <Form.Control
          type="text"
          value={value}
          readOnly
          isInvalid={!!error}
        />         
        : 
        <Form.Control
          as="textarea"
          rows={value.length - (value.replace(/\n/g,'')).length + 1}
          value={value}
          readOnly
          isInvalid={!!error}
        />
      }
      <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
      </Col>
    </Form.Group>
  );

  const renderReviewSection = (title: string, data: { [key: string]: string }) => (
    <Card className="mb-3">
      <Card.Header as="h5">{title}</Card.Header>
      <Card.Body>
        {Object.entries(data).map(([key, value]) => renderField(key, value || 'Not provided', errors[key]))}
      </Card.Body>
    </Card>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Form>
            <h3>Product Type</h3>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={4} className='inputPrompt'>
                Which Product Type are you creating a Reimbursement Request for?
              </Form.Label>
              <Col sm={3}>
                <Form.Select
                  value={policyType}
                  onChange={(e) => setPolicyType(e.target.value)}
                  onBlur={() => {
                    const updatedErrors = {...errors};
                    // Reset the Error Message and let Validation handle it
                    setErrorMessage('');
                    if (!policyType || policyType === '') {
                      updatedErrors.policyType = "Product type is required";
                    } else {
                      delete updatedErrors.policyType;
                    }
                    setErrors(updatedErrors);
                    updateErrorsSummary(updatedErrors);
                  }}
                  isInvalid={!!errors.policyType}
                  isValid={policyType !== '' && policyType !== 'Select Product Type'}
                  required
                  className="required-field"
                >
                  <option value="">Select Product Type</option>
                  <option value="Security Deposit">Security Deposit</option>
                  <option value="Tenant Legal Liability">Tenant Legal Liability</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.policyType}</Form.Control.Feedback>
              </Col>
            </Form.Group>
          </Form>
        );        
      case 2:
        if (policyType === 'Security Deposit') {
          return (
            <iframe
              title="Security Deposit Reimbursement Request"
              src="https://app.smartsheet.com/b/form/71604dc2f64a402ca457e2a4795db3a7"
              width="100%"
              height="500px"
              frameBorder="0"
            ></iframe>
          );
        } else {
          return (
            <Form>
              <h3>Event Info - {policyType}</h3>
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("Please provide the date this Event occurred.")}
              >
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={3} className='inputPrompt'>
                  Date of Event
                </Form.Label>
                <Col sm={2}>
                <Form.Control
                  type="date"
                  value={dateOfLoss}
                  onChange={(e) => setDateOfLoss(e.target.value)}
                  onBlur={() => {
                    const updatedErrors = {...errors};
                    if (!dateOfLoss) {
                      updatedErrors.dateOfLoss = "Date of Event is required";
                    } else {
                      delete updatedErrors.dateOfLoss;
                    }
                    setErrors(updatedErrors);
                    updateErrorsSummary(updatedErrors);
                  }}
                  isInvalid={!!errors.dateOfLoss}
                  isValid={dateOfLoss !== ''}
                  required
                  className="required-field"
                />
                  <Form.Control.Feedback type="invalid">{errors.dateOfLoss}</Form.Control.Feedback>
                </Col>
              </Form.Group>
              </OverlayTrigger>
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("Please provide a detailed description of what happened and the damages incurred.")}
              >
                <Form.Group as={Row} className="mb-3 inputPrompt">
                  <Form.Label column sm={3} className='inputPrompt'>
                    Brief Description of Event
                  </Form.Label>
                  <Col sm={5}>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      onBlur={() => {
                        const updatedErrors = {...errors};
                        if (!description) {
                          updatedErrors.description = "Description is required";
                        } else {
                          delete updatedErrors.description;
                        }
                        setErrors(updatedErrors);
                        updateErrorsSummary(updatedErrors);
                      }}                    
                      isInvalid={!!errors.description}
                      isValid={description !== ''}
                      required
                      className="required-field description-field"
                      placeholder="Please describe what happened..."
                    />
                    <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
                  </Col>
                </Form.Group>
              </OverlayTrigger>
              
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("Any additional information that may be helpful for processing your request.")}
              >
                <Form.Group as={Row} className="mb-3 inputPrompt">
                  <Form.Label column sm={3} className='inputPrompt'>Special Instructions:</Form.Label>
                  <Col sm={5}>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={specialInstructions}
                      onChange={(e) => setSpecialInstructions(e.target.value)}
                    />
                  </Col>
                </Form.Group>
              </OverlayTrigger>
              
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("Upload photos, documents, or other evidence related to your request. Maximum file size: 10MB per file, 25MB total.")}
              >
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm={3} className='inputPrompt'>Attachments:<br/>(Optional)</Form.Label>
                  <Col sm={9}>
                    <div className='inputLeft'>
                      <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        ref={fileInputRef}
                        accept="image/*,application/pdf"
                      />
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        ref={photoInputRef}
                      />
                      <Button variant="outline-primary" onClick={handleSelectFiles}>
                        Select Files
                      </Button>
                      {' '}
                      <Button variant="outline-secondary" onClick={handleTakePhoto}>
                        Take Photo
                      </Button>
                    </div>
                    {attachmentError && (
                      <Alert variant="danger" className="mt-2 mb-0">
                        {attachmentError}
                      </Alert>
                    )}
                    {attachments.length > 0 && (
                      <div className="mt-3">
                      <h6>
                        Uploaded Files
                        <small className="text-muted">
                          {' '}
                          (Total: {(attachments.reduce((t, f) => t + f.size, 0) / 1024 / 1024).toFixed(2)} MB)
                        </small>
                        :
                      </h6>
                        <ul className="list-group">
                          {attachments.map((file, index) => (
                            <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                              {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              <Button 
                                variant="danger" 
                                size="sm"
                                onClick={() => handleRemoveFile(index)}
                              >
                                Remove
                              </Button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Col>
                </Form.Group>
              </OverlayTrigger>
            </Form>
          );
        }
      case 3:
        return (
          <Form>
            <h3>Participant Information - {policyType}</h3>
            
            <OverlayTrigger
              placement="right"
              overlay={renderTooltip("Enter your addendum number as it appears on your Lease Addendum.")}
            >
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={3} className='inputPrompt'>
                  Addendum Number
                </Form.Label>
                <Col sm={5}>
                  <Form.Control
                    type="text"
                    value={policyNumber}
                    onChange={(e) => setPolicyNumber(e.target.value)}
                    onBlur={() => {
                      const updatedErrors = {...errors};
                      /*
                      if (!policyNumber) {
                        updatedErrors.name = "Policy Number is required";
                      } else {
                        delete updatedErrors.policyNumber;
                      }
                      */
                      setErrors(updatedErrors);
                      updateErrorsSummary(updatedErrors);
                    }}                  
                    isInvalid={!!errors.policyNumber}
                    isValid={policyNumber !== ''}
                    /* required */
                    className="" /*"required-field"*/
                  />
                  <Form.Control.Feedback type="invalid">{errors.policyNumber}</Form.Control.Feedback>
                </Col>
              </Form.Group>
            </OverlayTrigger>
            
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3} className='inputPrompt'>
                Name
              </Form.Label>
              <Col sm={5}>
                <Form.Control
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => {
                    const updatedErrors = {...errors};
                    if (!name) {
                      updatedErrors.name = "Name is required";
                    } else {
                      delete updatedErrors.name;
                    }
                    setErrors(updatedErrors);
                    updateErrorsSummary(updatedErrors);
                  }}
                  isInvalid={!!errors.name}
                  isValid={name !== ''}
                  required
                  className="required-field"
                />
                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
              </Col>
            </Form.Group>
            
            <OverlayTrigger
              placement="right"
              overlay={renderTooltip("Phone number where we can reach you regarding your Reimbursement Request. Format: (XXX) XXX-XXXX")}
            >
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={3} className='inputPrompt'>
                  Phone
                </Form.Label>
                <Col sm={5}>
                  <Form.Control
                    type="tel"
                    value={phone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handlePhoneChange(e, setPhone)}
                    onBlur={() => {
                      const updatedErrors = {...errors};
                      if (!phone) {
                        updatedErrors.phone = "Phone is required";
                      } else if (!validatePhone(phone)) {
                        updatedErrors.phone = "Please enter a valid phone number";
                      } else {
                        delete updatedErrors.phone;
                      }
                      setErrors(updatedErrors);
                      updateErrorsSummary(updatedErrors);
                    }}                  
                    isInvalid={!!errors.phone}
                    isValid={phone !== '' && validatePhone(phone)}
                    placeholder="(___) ___-____"
                    required
                    className="required-field"
                  />
                  <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                </Col>
              </Form.Group>
            </OverlayTrigger>
            
            <OverlayTrigger
              placement="right"
              overlay={renderTooltip("Please enter a valid email address where we can send Reimbursement updates.")}
            >
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={3} className='inputPrompt'>
                  Email
                </Form.Label>
                <Col sm={5}>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    isInvalid={!!errors.email}
                    isValid={email !== '' && validateEmail(email)}
                    required
                    className="required-field"
                    onBlur={() => {
                      // Validate just this field and update errors
                      const updatedErrors = {...errors};
                      if (!email) {
                        updatedErrors.email = "Email is required";
                      } else if (!validateEmail(email)) {
                        updatedErrors.email = "Please enter a valid email address";
                      } else {
                        delete updatedErrors.email;
                      }
                      
                      // Check if emails match
                      if (email && confirmEmail && email !== confirmEmail) {
                        updatedErrors.email = "Email addresses must match";
                        updatedErrors.confirmEmail = "Email addresses must match";
                      } else if (email && confirmEmail && email === confirmEmail) {
                        delete updatedErrors.confirmEmail;
                      }
                      
                      setErrors(updatedErrors);
                      updateErrorsSummary(updatedErrors);
                    }}
                  />
                  <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                </Col>
              </Form.Group>
            </OverlayTrigger>
            
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3} className='inputPrompt'>
                Confirm Email
              </Form.Label>
              <Col sm={5}>
                <Form.Control
                  type="email"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  isInvalid={!!errors.confirmEmail}
                  isValid={confirmEmail !== '' && email === confirmEmail}
                  required
                  className="required-field"
                  onBlur={() => {
                    // Validate just this field and update errors
                    const updatedErrors = {...errors};
                    if (!confirmEmail) {
                      updatedErrors.email = "Confirm Email is required";
                    } else if (!validateEmail(confirmEmail)) {
                      updatedErrors.confirmEmail = "Please enter a valid confirmation email address";
                    } else {
                      delete updatedErrors.confirmEmail;
                    }
                    
                    // Check if emails match
                    if (email && confirmEmail && email !== confirmEmail) {
                      updatedErrors.email = "Email addresses must match";
                      updatedErrors.confirmEmail = "Email addresses must match";
                    } else if (email && confirmEmail && email === confirmEmail) {
                      delete updatedErrors.confirmEmail;
                    }
                    
                    setErrors(updatedErrors);
                    updateErrorsSummary(updatedErrors);
                  }}
              />
                <Form.Control.Feedback type="invalid">{errors.confirmEmail}</Form.Control.Feedback>
              </Col>
            </Form.Group>
            
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3} className='inputPrompt'>
                Street Address
              </Form.Label>
              <Col sm={5}>
                <Form.Control
                  type="text"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  onBlur={() => {
                    const updatedErrors = {...errors};
                    if (!street) {
                      updatedErrors.street = "Address is required";
                    } else {
                      delete updatedErrors.street;
                    }
                    setErrors(updatedErrors);
                    updateErrorsSummary(updatedErrors);
                  }}                
                  isInvalid={!!errors.street}
                  isValid={street !== ''}
                  required
                  className="required-field"
                />
                <Form.Control.Feedback type="invalid">{errors.street}</Form.Control.Feedback>
              </Col>
            </Form.Group>
            
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3} className='inputPrompt'>
                City, State, Zip
              </Form.Label>
              <Col md={4}>
                <Form.Control
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  onBlur={() => {
                    const updatedErrors = {...errors};
                    if (!city) {
                      updatedErrors.city = "City is required";
                    } else {
                      delete updatedErrors.city;
                    }
                    setErrors(updatedErrors);
                    updateErrorsSummary(updatedErrors);
                  }}                
                  isInvalid={!!errors.city}
                  isValid={city !== ''}
                  className='inputleft required-field'
                  placeholder="City"
                  required
                />
                <Form.Control.Feedback type="invalid">{errors.city}</Form.Control.Feedback>    
              </Col>
              <Col md={2}>
                <Form.Select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  onBlur={() => {
                    const updatedErrors = {...errors};
                    if (!state) {
                      updatedErrors.state = "State is required";
                    } else {
                      delete updatedErrors.state;
                    }
                    setErrors(updatedErrors);
                    updateErrorsSummary(updatedErrors);
                  }}                
                  isInvalid={!!errors.state}
                  isValid={state !== ''}
                  required
                  className="required-field"
                >
                  <option value="">State</option>
                  {states.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.state}</Form.Control.Feedback>
              </Col>
              <Col md={2}>
                <Form.Control
                  type="text"
                  value={zip}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleZipChange(e, setZip)}
                  onBlur={() => {
                    const updatedErrors = {...errors};
                    if (!zip) {
                      updatedErrors.zip = "ZIP code is required";
                    } else if (!validateZipCode(zip)) {
                      updatedErrors.zip = "Please enter a valid ZIP code";
                    } else {
                      delete updatedErrors.zip;
                    }
                    setErrors(updatedErrors);
                    updateErrorsSummary(updatedErrors);
                  }}               
                  isInvalid={!!errors.zip}
                  isValid={zip !== '' && validateZipCode(zip)}
                  placeholder="_____-____"
                  required
                  className="required-field"
                />
                <Form.Control.Feedback type="invalid">{errors.zip}</Form.Control.Feedback>
              </Col>
            </Form.Group>
          </Form>
        );
      case 4:
        return (
          <Form>
            <h3>Additional Participant Information (Optional) - {policyType}</h3>
            <p className="text-muted mb-3">
              Fill this section only if the Participant is different from the Lease Addendum holder. 
              If you start entering information in this section, all fields become required.
            </p>
            
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3} className='inputPrompt'>
                Name {errors.claimantName && <span className="text-danger">*</span>}
              </Form.Label>
              <Col sm={5}>
                <Form.Control
                  type="text"
                  value={claimantName}
                  onChange={(e) => setClaimantName(e.target.value)}
                  isInvalid={!!errors.claimantName}
                  isValid={claimantName !== '' && !errors.claimantName}
                  className={claimantName || claimantPhone || claimantEmail || claimantStreet ? "required-field" : ""}
                />
                <Form.Control.Feedback type="invalid">{errors.claimantName}</Form.Control.Feedback>
              </Col>
            </Form.Group>
            
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3} className='inputPrompt'>
                Phone {errors.claimantPhone && <span className="text-danger">*</span>}
              </Form.Label>
              <Col sm={5}>
                <Form.Control
                  type="tel"
                  value={claimantPhone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handlePhoneChange(e, setClaimantPhone)}
                  isInvalid={!!errors.claimantPhone}
                  isValid={claimantPhone !== '' && validatePhone(claimantPhone) && !errors.claimantPhone}
                  placeholder="(___) ___-____"
                  className={claimantName || claimantPhone || claimantEmail || claimantStreet ? "required-field" : ""}
                />
                <Form.Control.Feedback type="invalid">{errors.claimantPhone}</Form.Control.Feedback>
              </Col>
            </Form.Group>
            
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3} className='inputPrompt'>
                Email {errors.claimantEmail && <span className="text-danger">*</span>}
              </Form.Label>
              <Col sm={5}>
                <Form.Control
                  type="email"
                  value={claimantEmail}
                  onChange={(e) => setClaimantEmail(e.target.value)}
                  isInvalid={!!errors.claimantEmail}
                  isValid={claimantEmail !== '' && validateEmail(claimantEmail) && !errors.claimantEmail}
                  className={claimantName || claimantPhone || claimantEmail || claimantStreet ? "required-field" : ""}
                />
                <Form.Control.Feedback type="invalid">{errors.claimantEmail}</Form.Control.Feedback>
              </Col>
            </Form.Group>
            
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3} className='inputPrompt'>
                Confirm Email {errors.claimantConfirmEmail && <span className="text-danger">*</span>}
              </Form.Label>
              <Col sm={5}>
                <Form.Control
                  type="email"
                  value={claimantConfirmEmail}
                  onChange={(e) => setClaimantConfirmEmail(e.target.value)}
                  isInvalid={!!errors.claimantConfirmEmail}
                  isValid={claimantConfirmEmail !== '' && claimantEmail === claimantConfirmEmail && !errors.claimantConfirmEmail}
                  className={claimantName || claimantPhone || claimantEmail || claimantStreet ? "required-field" : ""}
                />
                <Form.Control.Feedback type="invalid">{errors.claimantConfirmEmail}</Form.Control.Feedback>
              </Col>
            </Form.Group>
            
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3} className='inputPrompt'>
                Street Address {errors.claimantStreet && <span className="text-danger">*</span>}
              </Form.Label>
              <Col sm={5}>
                <Form.Control
                  type="text"
                  value={claimantStreet}
                  onChange={(e) => setClaimantStreet(e.target.value)}
                  isInvalid={!!errors.claimantStreet}
                  isValid={claimantStreet !== '' && !errors.claimantStreet}
                  className={claimantName || claimantPhone || claimantEmail || claimantStreet ? "required-field" : ""}
                />
                <Form.Control.Feedback type="invalid">{errors.claimantStreet}</Form.Control.Feedback>
              </Col>
            </Form.Group>
            
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3} className='inputPrompt'>
                City, State, Zip {(errors.claimantCity || errors.claimantState || errors.claimantZip) && <span className="text-danger">*</span>}
              </Form.Label>
              <Col sm={4}>
                <Form.Control
                  type="text"
                  value={claimantCity}
                  onChange={(e) => setClaimantCity(e.target.value)}
                  isInvalid={!!errors.claimantCity}
                  isValid={claimantCity !== '' && !errors.claimantCity}
                  className={claimantName || claimantPhone || claimantEmail || claimantStreet ? "required-field" : ""}
                  placeholder="City"
                />
                <Form.Control.Feedback type="invalid">{errors.claimantCity}</Form.Control.Feedback>
              </Col>
              <Col sm={2}>
                <Form.Select
                  value={claimantState}
                  onChange={(e) => setClaimantState(e.target.value)}
                  isInvalid={!!errors.claimantState}
                  isValid={claimantState !== '' && !errors.claimantState}
                  className={claimantName || claimantPhone || claimantEmail || claimantStreet ? "required-field" : ""}
                >
                  <option value="">State</option>
                  {states.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.claimantState}</Form.Control.Feedback>
              </Col>
              <Col sm={2}>
                <Form.Control
                  type="text"
                  value={claimantZip}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleZipChange(e, setClaimantZip)}
                  isInvalid={!!errors.claimantZip}
                  isValid={claimantZip !== '' && validateZipCode(claimantZip) && !errors.claimantZip}
                  placeholder="_____-____"
                  className={claimantName || claimantPhone || claimantEmail || claimantStreet ? "required-field" : ""}
                />
                <Form.Control.Feedback type="invalid">{errors.claimantZip}</Form.Control.Feedback>
              </Col>
            </Form.Group>
          </Form>
        );
      case 5:
        return (
          <div>
            <h3>Review Your Reimbursement Request - {policyType}</h3>
            <p className="text-muted mb-3">
              Please review all information below before submitting your Reimbursement REquest.
              If you need to make changes, use the navigation tabs or the Back button.
            </p>
            
            {renderReviewSection("Step 1: Reimbursement Request Information", {
              "Event Date:": dateOfLoss,
              "Event Description:": description,
              "Special Instructions:": specialInstructions,
              "Attachments:": attachments.length > 0 ? attachments.map(file => file.name).join(', ') : 'No attachments'
            })}
            {renderReviewSection("Step 2: Participant Information", {
              "Addendum Number:": policyNumber,
              "Product Type:": policyType,
              "Name:": name,
              "Phone:": phone,
              "Email:": email,
              "Address:": `${street}, ${city} ${state} ${zip}`
            })}
            {renderReviewSection("Step 3: Additional Participant Information (Optional)", {
              "Name:": claimantName,
              "Phone:": claimantPhone,
              "Email:": claimantEmail,
              "Address:": claimantStreet ? `${claimantStreet}, ${claimantCity} ${claimantState} ${claimantZip}` : ''
            })}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <h1 className="my-4">Reimbursement Request</h1>
      
      <Nav variant="tabs" className="mb-3">
        {steps.map((step, index) => (
          <Nav.Item key={index}>
            <Nav.Link
              active={currentStep === index + 1}
              onClick={() => setCurrentStep(index + 1)}
            >
              Step {index + 1}: {step}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      
      {/* Main content area */}
      {renderStepContent()}
      
      {/* Error summary section - only show when there are errors AND user has attempted next */}
      {/* {attemptedNext && (errorsSummary.length > 0 || errorMessage || attachmentError) ? ( */}
      {showErrorSummary && (errorsSummary.length > 0 || errorMessage || attachmentError) &&
        <Alert variant="danger" className="mt-4" id="error-summary">
          <Alert.Heading>Please correct the following issues:</Alert.Heading>
          {errorMessage && <p className="mb-0">{errorMessage}</p>}
          {attachmentError && <p className="mb-0">{attachmentError}</p>}
          {errorsSummary.length > 0 && (
            <ul className="mb-0 mt-2">
              {errorsSummary.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          )}
        </Alert>
      }
      
      {/* Navigation buttons */}
      <div className="d-flex justify-content-between mt-4">
        <Button 
          variant="secondary" 
          onClick={handleBack} 
          className={currentStep > 1 && !isSubmitted && !isModalClosed ? "buttonShow" : "buttonHidden"}
        >
          Back
        </Button>
        
        <div className="ms-auto">
          {currentStep < steps.length && !isSubmitted && !isModalClosed && (
            <Button 
              variant="primary" 
              onClick={handleNext}
              className="buttonShow"
            >
              Next
            </Button>
          )}
          
          {currentStep === steps.length && !isSubmitted && !isModalClosed && !(policyType === 'Security Deposit') && (
            <Button 
              variant="success" 
              onClick={handleSubmit}
              className="buttonShow"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Processing...
                </>
              ) : (
                'Submit Reimbursement Request'
              )}
            </Button>
          )}
          
          {((isSubmitted && isModalClosed) || (policyType === 'Security Deposit' && currentStep !== 1)) && (
            <Button 
              variant="primary" 
              onClick={handleNewClaim}
              className="buttonShow"
            >
              Start New Reimbursement Request
            </Button>
          )}
        </div>
      </div>
      
      {/* Processing overlay */}
      {isSubmitting && (
        <div className="processing-overlay">
          <div className="processing-content">
            <div className="spinner-border processing-spinner text-primary" role="status">
              <span className="visually-hidden">Processing...</span>
            </div>
            <h4>Processing Your Reimbursement Request</h4>
            <p>Please wait while we submit your Reimbursement Request. This may take a few moments...</p>
          </div>
        </div>
      )}
      
      {/* Submission result modal */}
      <Modal show={showModal} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{isSubmitted ? "Reimbursement Request Submitted" : "Submission Error"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isSubmitted 
            ? "Your Reimbursement Request has been submitted successfully. You will receive a confirmation email shortly." 
            : (
              <>
                <p>There was an error submitting your Reimbursment Request:</p>
                <Alert variant="danger">
                  {apiErrorDetails || "Unknown error occurred. Please try again later."}
                </Alert>
                <p>If this problem persists, please contact our support team.</p>
              </>
            )
          }
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleModalClose}>
            {isSubmitted ? "Close" : "Try Again"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Reimbursement;

